import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Dealer Signup Service
 * Handles all API calls related to public dealer sign-up flow
 */
const DealerSignupService = {
  /**
   * Validate installer referral code (ID or email)
   * @param {string|number} code - Installer ID or email
   * @returns {Promise} - { valid: boolean, installerId: number, installerName: string }
   */
  validateInstallerReferral: async (code) => {
    const isNumeric = /^\d+$/.test(code);
    const endpoint = isNumeric
      ? `/public/validate-installer/${code}`
      : `/public/validate-installer-email/${code}`;

    const response = await axios.get(`${API_URL}${endpoint}`);
    return response.data;
  },

  /**
   * Check if email already exists in system
   * @param {string} email
   * @returns {Promise} - { exists: boolean, message: string }
   */
  checkEmailExists: async (email) => {
    const response = await axios.get(`${API_URL}/public/dealer-signup/validate-email`, {
      params: { email }
    });
    return response.data;
  },

  /**
   * Submit complete dealer signup
   * Creates dealer, user, Cognito account, and triggers proximity calculations.
   * For Assist signups (attributionSource === 'assist'), payload includes
   * assistTermsAccepted/assistTermsVersion and omits referring-installer fields
   * (preferred installer is assigned server-side by proximity).
   * @param {Object} signupData
   * @returns {Promise} - { success: boolean, dealerId: number, userId: number, message: string }
   */
  submitDealerSignup: async (signupData) => {
    const isAssist = signupData.attributionSource === 'assist';

    const payload = {
      // Dealer information (matches POST /dealers structure)
      dealerName: signupData.businessName,
      dealerEmail: signupData.contactEmail,
      dealerPhoneNumber: signupData.contactPhone,
      dealerAddressLine1: signupData.addressLine1,
      dealerAddressLine2: signupData.addressLine2 || '',
      dealerAddressCity: signupData.city,
      dealerAddressState: signupData.state,
      dealerAddressZip: signupData.zipCode,
      dealerActive: 1,
      assetType: signupData.assetType || 'auto',
      customerContract: 'N',

      // Straight from the selected Google place. place_id is a stable key for the
      // physical business — the server dedupes on it ahead of the lat/long radius match.
      // latitude/longitude let the server skip geocoding the address entirely.
      // All three are null if the user never picked from the autocomplete.
      //
      // Snake_case here is deliberate and matches the server column
      // `dealership.place_id` (which in turn matches `installer_location.place_id`),
      // even though the rest of this payload is camelCase.
      place_id: signupData.placeId || null,
      latitude: signupData.latitude ?? null,
      longitude: signupData.longitude ?? null,

      // Service configuration
      // If installer referral: use their categories, otherwise default to "Paint"
      installerCategories: signupData.installerCategories || 'Paint',

      // User information (matches POST /users structure)
      userName: signupData.contactName,
      userEmail: signupData.contactEmail,
      userPhoneNumber: signupData.contactPhone,
      userType: 'dealer',
      access: 'STANDARD',
      active: 1,

      // Attribution
      attributionSource: signupData.attributionSource // 'direct', 'internal', 'installer', 'assist'
    };

    if (isAssist) {
      // Assist signups carry terms acceptance; no referring installer
      payload.assistTermsAccepted = signupData.assistTermsAccepted === true;
      payload.assistTermsVersion = signupData.assistTermsVersion;
    } else {
      // Standard / referral signups
      payload.referringInstallerId = signupData.referringInstallerId || null;
      payload.referralCode = signupData.referralCode || null;
      payload.markPreferredInstaller = signupData.referringInstallerId ? true : false;
    }

    const response = await axios.post(`${API_URL}/public/dealer-signup`, payload);
    return response.data;
  },

  /**
   * Get installer categories for a given installer ID
   * Used to determine which services to configure when signing up via installer referral
   * @param {number} installerId
   * @returns {Promise} - { installerCategories: string }
   */
  getInstallerCategories: async (installerId) => {
    const response = await axios.get(`${API_URL}/public/installer-categories/${installerId}`);
    return response.data;
  }
};

export default DealerSignupService;
